import { useState, useEffect, useCallback, useRef } from 'react';
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Course } from './CourseModal';

export interface TeeBox {
  id: string;
  course_id: string;
  name: string;
  color: string;
  player_categories: string[];
  course_rating: number | null;
  slope_rating: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const TEE_COLORS = [
  { value: 'black', label: 'Black', bg: 'bg-gray-900', ring: 'ring-gray-900' },
  { value: 'gold', label: 'Gold', bg: 'bg-yellow-500', ring: 'ring-yellow-500' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-600', ring: 'ring-blue-600' },
  { value: 'white', label: 'White', bg: 'bg-white border border-gray-300', ring: 'ring-gray-400' },
  { value: 'red', label: 'Red', bg: 'bg-red-500', ring: 'ring-red-500' },
  { value: 'green', label: 'Green', bg: 'bg-green-600', ring: 'ring-green-600' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-300', ring: 'ring-yellow-300' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500', ring: 'ring-orange-500' },
];

const COLOR_DOT: Record<string, string> = {
  black: 'bg-gray-900',
  gold: 'bg-yellow-500',
  blue: 'bg-blue-600',
  white: 'bg-white border border-gray-300',
  red: 'bg-red-500',
  green: 'bg-green-600',
  yellow: 'bg-yellow-300',
  orange: 'bg-orange-500',
};

interface TeeBoxForm {
  name: string;
  color: string;
  player_categories: string;
  course_rating: string;
  slope_rating: string;
}

function defaultForm(): TeeBoxForm {
  return { name: '', color: 'white', player_categories: '', course_rating: '', slope_rating: '' };
}

function teeToForm(t: TeeBox): TeeBoxForm {
  return {
    name: t.name,
    color: t.color,
    player_categories: t.player_categories.join(', '),
    course_rating: t.course_rating != null ? String(t.course_rating) : '',
    slope_rating: t.slope_rating != null ? String(t.slope_rating) : '',
  };
}

interface Props {
  course: Course;
  onTeeBoxesChange?: (tees: TeeBox[]) => void;
}

export default function TeeBoxManager({ course, onTeeBoxesChange }: Props) {
  const [teeBoxes, setTeeBoxes] = useState<TeeBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<TeeBoxForm>(defaultForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const onTeeBoxesChangeRef = useRef(onTeeBoxesChange);
  useEffect(() => {
    onTeeBoxesChangeRef.current = onTeeBoxesChange;
  });

  const fetchTeeBoxes = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('tee_boxes')
      .select('*')
      .eq('course_id', course.id)
      .order('sort_order');
    if (error) {
      toast.error('Failed to load tee boxes');
    } else {
      const boxes = (data as TeeBox[]) ?? [];
      setTeeBoxes(boxes);
      onTeeBoxesChangeRef.current?.(boxes);
    }
    setLoading(false);
  }, [course.id]);

  useEffect(() => {
    fetchTeeBoxes();
  }, [fetchTeeBoxes]);

  const startNew = () => {
    setForm(defaultForm());
    setEditingId('new');
  };

  const startEdit = (t: TeeBox) => {
    setForm(teeToForm(t));
    setEditingId(t.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(defaultForm());
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Tee box name is required');
      return;
    }
    setSaving(true);
    const cats = form.player_categories
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      course_id: course.id,
      name: form.name.trim(),
      color: form.color,
      player_categories: cats,
      course_rating: form.course_rating ? Number(form.course_rating) : null,
      slope_rating: form.slope_rating ? Number(form.slope_rating) : null,
      sort_order: editingId === 'new' ? teeBoxes.length : (teeBoxes.find((t) => t.id === editingId)?.sort_order ?? 0),
      updated_at: new Date().toISOString(),
    };

    try {
      if (editingId === 'new') {
        const { data, error } = await supabase.from('tee_boxes').insert(payload).select().single();
        if (error) throw error;
        const updated = [...teeBoxes, data as TeeBox];
        setTeeBoxes(updated);
        onTeeBoxesChangeRef.current?.(updated);
        toast.success('Tee box added');
      } else {
        const { data, error } = await supabase
          .from('tee_boxes')
          .update(payload)
          .eq('id', editingId)
          .select()
          .single();
        if (error) throw error;
        const updated = teeBoxes.map((t) => (t.id === editingId ? (data as TeeBox) : t));
        setTeeBoxes(updated);
        onTeeBoxesChangeRef.current?.(updated);
        toast.success('Tee box updated');
      }
      setEditingId(null);
      setForm(defaultForm());
    } catch (err) {
      console.error(err);
      toast.error('Failed to save tee box');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from('tee_boxes').delete().eq('id', id);
    if (error) {
      toast.error('Failed to delete tee box');
    } else {
      const updated = teeBoxes.filter((t) => t.id !== id);
      setTeeBoxes(updated);
      onTeeBoxesChangeRef.current?.(updated);
      toast.success('Tee box removed');
      if (editingId === id) cancelEdit();
    }
    setDeletingId(null);
  };

  const moveUp = async (index: number) => {
    if (index === 0) return;
    const reordered = [...teeBoxes];
    [reordered[index - 1], reordered[index]] = [reordered[index], reordered[index - 1]];
    const updates = reordered.map((t, i) => supabase.from('tee_boxes').update({ sort_order: i }).eq('id', t.id));
    await Promise.all(updates);
    const refreshed = reordered.map((t, i) => ({ ...t, sort_order: i }));
    setTeeBoxes(refreshed);
    onTeeBoxesChangeRef.current?.(refreshed);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Tee Boxes</h3>
          <p className="text-xs text-gray-400 mt-0.5">Define tee boxes, assign player categories and ratings</p>
        </div>
        {editingId === null && (
          <button
            onClick={startNew}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-golf-orange hover:bg-golf-orange/90 rounded-lg transition-colors"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add Tee Box
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-golf-orange border-t-transparent" />
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {teeBoxes.length === 0 && editingId === null && (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-gray-400">No tee boxes defined yet.</p>
              <button
                onClick={startNew}
                className="mt-3 text-sm text-golf-orange hover:underline font-medium"
              >
                Add the first tee box
              </button>
            </div>
          )}

          {teeBoxes.map((tee, idx) => (
            <div key={tee.id}>
              {editingId === tee.id ? (
                <TeeBoxForm
                  form={form}
                  onChange={setForm}
                  onSave={handleSave}
                  onCancel={cancelEdit}
                  saving={saving}
                />
              ) : (
                <div className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors group">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="text-gray-300 hover:text-gray-500 disabled:opacity-20 disabled:cursor-not-allowed leading-none text-xs"
                    >
                      ▲
                    </button>
                  </div>
                  <div className={`w-5 h-5 rounded-full flex-shrink-0 ${COLOR_DOT[tee.color] ?? 'bg-gray-200'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{tee.name}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      {tee.player_categories.length > 0 && (
                        <span className="text-xs text-gray-500">
                          {tee.player_categories.join(', ')}
                        </span>
                      )}
                      {tee.course_rating && (
                        <span className="text-xs text-gray-400">CR {tee.course_rating}</span>
                      )}
                      {tee.slope_rating && (
                        <span className="text-xs text-gray-400">SR {tee.slope_rating}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => startEdit(tee)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(tee.id)}
                      disabled={deletingId === tee.id}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {editingId === 'new' && (
            <TeeBoxForm
              form={form}
              onChange={setForm}
              onSave={handleSave}
              onCancel={cancelEdit}
              saving={saving}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface TeeBoxFormProps {
  form: TeeBoxForm;
  onChange: (f: TeeBoxForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}

function TeeBoxForm({ form, onChange, onSave, onCancel, saving }: TeeBoxFormProps) {
  return (
    <div className="px-5 py-4 bg-gray-50 border-t border-b border-gray-100 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Tee Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => onChange({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
            placeholder="e.g. Championship"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
          <div className="flex flex-wrap gap-1.5">
            {TEE_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => onChange({ ...form, color: c.value })}
                title={c.label}
                className={`w-6 h-6 rounded-full transition-all ${c.bg} ${
                  form.color === c.value ? `ring-2 ring-offset-1 ${c.ring} scale-110` : 'hover:scale-105'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Player Categories
          <span className="text-gray-400 font-normal ml-1">(comma-separated)</span>
        </label>
        <input
          type="text"
          value={form.player_categories}
          onChange={(e) => onChange({ ...form, player_categories: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
          placeholder="e.g. Pro, Senior Pro, Amateur A"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Course Rating <span className="text-gray-400 font-normal">(USGA)</span>
          </label>
          <input
            type="number"
            value={form.course_rating}
            onChange={(e) => onChange({ ...form, course_rating: e.target.value })}
            step="0.1"
            min="55"
            max="80"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
            placeholder="e.g. 72.4"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Slope Rating</label>
          <input
            type="number"
            value={form.slope_rating}
            onChange={(e) => onChange({ ...form, slope_rating: e.target.value })}
            step="0.5"
            min="55"
            max="155"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none text-sm"
            placeholder="e.g. 113.0"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <XMarkIcon className="w-3.5 h-3.5" />
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-golf-orange hover:bg-golf-orange/90 rounded-lg transition-colors disabled:opacity-50"
        >
          {saving ? (
            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <CheckIcon className="w-3.5 h-3.5" />
          )}
          Save
        </button>
      </div>
    </div>
  );
}
